import React from 'react';
import './Services.css';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: '⚖️',
      title: 'Civil Litigation',
      description: 'Expert representation in civil disputes, property matters, and commercial litigation with comprehensive legal support.',
      features: ['Property Disputes', 'Contract Disputes', 'Commercial Litigation', 'Tort Claims'],
      color: 'var(--accent-blue)'
    },
    {
      icon: '🏛️',
      title: 'Criminal Defense',
      description: 'Skilled defense representation in criminal cases with thorough case preparation and courtroom advocacy.',
      features: ['Criminal Appeals', 'Bail Applications', 'Trial Defense', 'Legal Consultation'],
      color: 'var(--accent-teal)'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Family Law',
      description: 'Compassionate handling of family matters including divorce, custody, and matrimonial disputes.',
      features: ['Divorce Proceedings', 'Child Custody', 'Maintenance Claims', 'Matrimonial Disputes'],
      color: 'var(--accent-purple)'
    },
    {
      icon: '🏢',
      title: 'Corporate Law',
      description: 'Business legal services including contract drafting, compliance, and corporate governance matters.',
      features: ['Contract Drafting', 'Corporate Compliance', 'Business Registration', 'Legal Advisory'],
      color: 'var(--accent-orange)'
    },
    {
      icon: '🏠',
      title: 'Property Law',
      description: 'Complete property legal services including documentation, disputes, and real estate transactions.',
      features: ['Property Documentation', 'Title Verification', 'Real Estate Transactions', 'Property Disputes'],
      color: 'var(--primary-gold)'
    },
    {
      icon: '📋',
      title: 'Legal Consultation',
      description: 'Professional legal advice and consultation services for various legal matters and concerns.',
      features: ['Legal Advisory', 'Document Review', 'Case Assessment', 'Strategic Planning'],
      color: 'var(--accent-blue)'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero full-width-section">
        <div className="services-hero-full-width">
          <div className="section-container">
            <div className="services-hero-content">
              <h1 className="page-title">Legal Services</h1>
              <p className="page-subtitle">
                Comprehensive legal solutions tailored to meet your specific needs with professional excellence
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-main full-width-section">
        <div className="section-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-header">
                  <div className="service-icon" style={{'--service-color': service.color}}>
                    {service.icon}
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                </div>
                <p className="service-description">{service.description}</p>
                <div className="service-features">
                  <h4>Key Services:</h4>
                  <ul>
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
                <div className="service-action">
                  <Link to="/contact" className="btn btn-outline">
                    Get Consultation
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section full-width-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Our Legal Process</h2>
            <p className="section-subtitle">
              A systematic approach to delivering exceptional legal services
            </p>
          </div>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Initial Consultation</h3>
                <p>We begin with a comprehensive consultation to understand your legal needs and assess your case.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Case Analysis</h3>
                <p>Thorough analysis of your case, including legal research and strategy development.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Legal Strategy</h3>
                <p>Development of a customized legal strategy tailored to achieve the best possible outcome.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3>Execution & Support</h3>
                <p>Implementation of the legal strategy with continuous support and regular updates.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-services full-width-section">
        <div className="section-container">
          <div className="why-choose-content">
            <div className="why-choose-text">
              <h2 className="section-title">Why Choose Our Legal Services?</h2>
              <div className="title-underline"></div>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">🎯</div>
                  <div className="benefit-content">
                    <h4>Personalized Approach</h4>
                    <p>Every case receives individual attention with customized legal strategies.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <div className="benefit-content">
                    <h4>Efficient Resolution</h4>
                    <p>Focus on timely and cost-effective resolution of legal matters.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🛡️</div>
                  <div className="benefit-content">
                    <h4>Strong Advocacy</h4>
                    <p>Aggressive representation to protect your rights and interests.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📞</div>
                  <div className="benefit-content">
                    <h4>24/7 Support</h4>
                    <p>Available for urgent legal matters and client support when needed.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-choose-stats">
              <div className="service-stat">
                <div className="stat-number">15+</div>
                <div className="stat-label">Years Experience</div>
              </div>
              <div className="service-stat">
                <div className="stat-number">500+</div>
                <div className="stat-label">Cases Handled</div>
              </div>
              <div className="service-stat">
                <div className="stat-number">95%</div>
                <div className="stat-label">Success Rate</div>
              </div>
              <div className="service-stat">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Client Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta full-width-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Ready to Get Legal Help?</h2>
            <p>Contact us today for a free consultation and let us help you with your legal needs.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">Schedule Consultation</Link>
              <Link to="/practice-areas" className="btn btn-secondary">View Practice Areas</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
